import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface SlackAddChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SlackAddChannelModal({
  isOpen,
  onClose,
}: SlackAddChannelModalProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-[450px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Entelligence.AI to a Channel</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-[14px]">
          {/* Description */}
          <p className="text-2xs text-ds-text-subtle">
            To add Entelligence.AI to a new Slack channel, follow these simple
            steps:
          </p>

          {/* Steps */}
          <div className="flex flex-col gap-[14px]">
            {/* Step 1 */}
            <div className="flex gap-[10.5px] items-start">
              <div className="w-[21px] h-[21px] rounded-full flex items-center justify-center bg-ds-gray-subtlest">
                <span className="text-3xs font-medium text-ds-text-darker">
                  1
                </span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium leading-[21px] text-ds-text-darker">
                  Go to your Slack channel
                </p>
                <p className="text-2xs text-ds-text-subtle">
                  Navigate to the channel where you want to add the bot
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-[10.5px] items-start">
              <div className="w-[21px] h-[21px] rounded-full flex items-center justify-center bg-ds-gray-subtlest">
                <span className="text-3xs font-medium text-ds-text-darker">
                  2
                </span>
              </div>
              <div className="flex-1 flex flex-col gap-[3.5px]">
                <p className="text-sm font-medium leading-[21px] text-ds-text-darker">
                  Mention the bot for private channel
                </p>
                <div className="w-fit px-[7px] py-[7px] rounded-[7.5px] border bg-ds-surface-white border-ds-border-subtler">
                  <span className="text-sm font-medium leading-[17.5px] text-ds-text-darker">
                    @Entelligence.AI
                  </span>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-[10.5px] items-start">
              <div className="w-[21px] h-[21px] rounded-full flex items-center justify-center bg-ds-gray-subtlest">
                <span className="text-3xs font-medium text-ds-text-darker">
                  3
                </span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium leading-[21px] text-ds-text-darker">
                  Bot will join automatically
                </p>
                <p className="text-2xs text-ds-text-subtle">
                  The bot will join the channel and guide you through setup
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-[10.5px] items-start">
              <div className="w-[21px] h-[21px] rounded-full flex items-center justify-center bg-ds-gray-subtlest">
                <span className="text-3xs font-medium text-ds-text-darker">
                  4
                </span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium leading-[21px] text-ds-text-darker">
                  Configure notifications
                </p>
                <p className="text-2xs text-ds-text-subtle">
                  Use commands like /subscribe, /sprint, /leaderboard to set up
                  notifications
                </p>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="flex gap-[7px] items-start p-[11.5px] rounded-[8.75px] border bg-ds-surface-white border-ds-border-subtler">
            <svg
              className="w-[14px] h-[14px] mt-[1.75px] flex-shrink-0 text-ds-text-subtle"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="flex-1 text-2xs leading-[17px] text-ds-text-default">
              Once added, the channel will automatically appear in the
              &quot;Connected Channels&quot; list above.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
